// src/pages/Connector/PrayerTiming/JumahManagement.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    CircularProgress,
    Alert,
    TextField,
    Grid,
    Tabs,
    Tab,
    Tooltip,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    NavigateBefore,
    NavigateNext
} from '@mui/icons-material';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

function TabPanel({ children, value, index, ...other }) {
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
        </div>
    );
}

export default function JumahManagement({ open, onClose, config, chatbotId, canModify }) {
    const [activeTab, setActiveTab] = useState(0);
    const [jumahTimes, setJumahTimes] = useState([]);
    const [khateebs, setKhateebs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // For khateeb schedule
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // For adding/editing
    const [editingTime, setEditingTime] = useState(null);
    const [newTime, setNewTime] = useState({ khutbah_label: '', khutbah_time: '' });
    const [isAddingTime, setIsAddingTime] = useState(false);

    // For khateeb editing
    const [editingKhateeb, setEditingKhateeb] = useState(null);
    const [newKhateeb, setNewKhateeb] = useState({ date: '', khutbah_order: 1, khateeb_name: '' });
    const [isAddingKhateeb, setIsAddingKhateeb] = useState(false);

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 4 }, (_, i) => currentYear - 1 + i);

    // Fetch Juma'h times
    const fetchJumahTimes = useCallback(async () => {
        if (!config || !open) return;
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(
                `/api/prayer-timing/configurations/${config.id}/jumah-times`,
                { params: { chatbot_id: chatbotId } }
            );
            setJumahTimes(response.data.data || []);
        } catch (err) {
            console.error("Error fetching Juma'h times:", err);
            setError(err.response?.data?.message || "Failed to fetch Juma'h times.");
        } finally {
            setIsLoading(false);
        }
    }, [config, chatbotId, open]);

    // Fetch Khateebs
    const fetchKhateebs = useCallback(async () => {
        if (!config || !open) return;
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(
                `/api/prayer-timing/configurations/${config.id}/jumah-khateebs`,
                { params: { chatbot_id: chatbotId, month: selectedMonth, year: selectedYear } }
            );
            setKhateebs(response.data.data || []);
        } catch (err) {
            console.error("Error fetching khateebs:", err);
            setError(err.response?.data?.message || "Failed to fetch khateebs.");
        } finally {
            setIsLoading(false);
        }
    }, [config, chatbotId, open, selectedMonth, selectedYear]);

    useEffect(() => {
        if (activeTab === 0) {
            fetchJumahTimes();
        } else {
            fetchKhateebs();
        }
    }, [activeTab, fetchJumahTimes, fetchKhateebs]);

    // Clear messages after 5 seconds
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    // Juma'h Time handlers
    const handleAddJumahTime = async () => {
        if (!newTime.khutbah_time) {
            setError('Khutbah time is required');
            return;
        }
        try {
            await axiosInstance.post(
                `/api/prayer-timing/configurations/${config.id}/jumah-times`,
                newTime,
                { params: { chatbot_id: chatbotId } }
            );
            setSuccessMessage('Juma\'h time added successfully');
            setNewTime({ khutbah_label: '', khutbah_time: '' });
            setIsAddingTime(false);
            fetchJumahTimes();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add Juma\'h time');
        }
    };

    const handleUpdateJumahTime = async () => {
        try {
            await axiosInstance.put(
                `/api/prayer-timing/jumah-times/${editingTime.id}`,
                editingTime,
                { params: { chatbot_id: chatbotId } }
            );
            setSuccessMessage('Juma\'h time updated successfully');
            setEditingTime(null);
            fetchJumahTimes();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update Juma\'h time');
        }
    };

    const handleDeleteJumahTime = async (id) => {
        if (!window.confirm('Are you sure you want to delete this Juma\'h time?')) return;
        try {
            await axiosInstance.delete(
                `/api/prayer-timing/jumah-times/${id}`,
                { params: { chatbot_id: chatbotId } }
            );
            setSuccessMessage('Juma\'h time deleted successfully');
            fetchJumahTimes();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete Juma\'h time');
        }
    };

    // Khateeb handlers
    const handleAddKhateeb = async () => {
        if (!newKhateeb.date || !newKhateeb.khateeb_name) {
            setError('Date and khateeb name are required');
            return;
        }
        try {
            await axiosInstance.post(
                `/api/prayer-timing/configurations/${config.id}/jumah-khateebs`,
                newKhateeb,
                { params: { chatbot_id: chatbotId } }
            );
            setSuccessMessage('Khateeb added successfully');
            setNewKhateeb({ date: '', khutbah_order: 1, khateeb_name: '' });
            setIsAddingKhateeb(false);
            fetchKhateebs();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add khateeb');
        }
    };

    const handleUpdateKhateeb = async () => {
        try {
            await axiosInstance.put(
                `/api/prayer-timing/jumah-khateebs/${editingKhateeb.id}`,
                editingKhateeb,
                { params: { chatbot_id: chatbotId } }
            );
            setSuccessMessage('Khateeb updated successfully');
            setEditingKhateeb(null);
            fetchKhateebs();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update khateeb');
        }
    };

    const handleDeleteKhateeb = async (id) => {
        if (!window.confirm('Are you sure you want to delete this khateeb?')) return;
        try {
            await axiosInstance.delete(
                `/api/prayer-timing/jumah-khateebs/${id}`,
                { params: { chatbot_id: chatbotId } }
            );
            setSuccessMessage('Khateeb deleted successfully');
            fetchKhateebs();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete khateeb');
        }
    };

    // Get Fridays in selected month
    const getFridaysInMonth = () => {
        const fridays = [];
        const year = selectedYear;
        const month = selectedMonth - 1;
        const date = new Date(year, month, 1);

        while (date.getMonth() === month) {
            if (date.getDay() === 5) { // Friday
                fridays.push(new Date(date));
            }
            date.setDate(date.getDate() + 1);
        }
        return fridays;
    };

    // Format date to YYYY-MM-DD in local timezone (not UTC)
    const formatDateToISO = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        // Parse the date string and display in local format
        const parts = dateStr instanceof Date ? null : dateStr.split('-');
        const date = parts ? new Date(parts[0], parts[1] - 1, parts[2]) : dateStr;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Typography variant="h6">Juma'h Management - {config?.title}</Typography>
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                {successMessage && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
                        {successMessage}
                    </Alert>
                )}

                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label="Khutbah Times" />
                    <Tab label="Khateeb Schedule" />
                </Tabs>

                {/* Khutbah Times Tab */}
                <TabPanel value={activeTab} index={0}>
                    {canModify && (
                        <Box sx={{ mb: 2 }}>
                            {isAddingTime ? (
                                <Paper sx={{ p: 2 }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Label (e.g., 1st Khutbah)"
                                                value={newTime.khutbah_label}
                                                onChange={(e) => setNewTime({ ...newTime, khutbah_label: e.target.value })}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Time (e.g., 12:30)"
                                                value={newTime.khutbah_time}
                                                onChange={(e) => setNewTime({ ...newTime, khutbah_time: e.target.value })}
                                                size="small"
                                                placeholder="HH:MM"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Button
                                                variant="contained"
                                                onClick={handleAddJumahTime}
                                                startIcon={<SaveIcon />}
                                                sx={{ mr: 1 }}
                                            >
                                                Save
                                            </Button>
                                            <Button
                                                onClick={() => { setIsAddingTime(false); setNewTime({ khutbah_label: '', khutbah_time: '' }); }}
                                                startIcon={<CancelIcon />}
                                            >
                                                Cancel
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ) : (
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => setIsAddingTime(true)}
                                >
                                    Add Khutbah Time
                                </Button>
                            )}
                        </Box>
                    )}

                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : jumahTimes.length === 0 ? (
                        <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                            No Juma'h times configured yet.
                        </Typography>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Order</strong></TableCell>
                                        <TableCell><strong>Label</strong></TableCell>
                                        <TableCell><strong>Time</strong></TableCell>
                                        {canModify && <TableCell align="center"><strong>Actions</strong></TableCell>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {jumahTimes.map((jt) => (
                                        <TableRow key={jt.id}>
                                            {editingTime?.id === jt.id ? (
                                                <>
                                                    <TableCell>{jt.khutbah_order}</TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            value={editingTime.khutbah_label}
                                                            onChange={(e) => setEditingTime({ ...editingTime, khutbah_label: e.target.value })}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            value={editingTime.khutbah_time}
                                                            onChange={(e) => setEditingTime({ ...editingTime, khutbah_time: e.target.value })}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <IconButton size="small" color="primary" onClick={handleUpdateJumahTime}>
                                                            <SaveIcon />
                                                        </IconButton>
                                                        <IconButton size="small" onClick={() => setEditingTime(null)}>
                                                            <CancelIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </>
                                            ) : (
                                                <>
                                                    <TableCell>{jt.khutbah_order}</TableCell>
                                                    <TableCell>{jt.khutbah_label || `Khutbah ${jt.khutbah_order}`}</TableCell>
                                                    <TableCell>
                                                        <Chip label={jt.khutbah_time} color="primary" size="small" />
                                                    </TableCell>
                                                    {canModify && (
                                                        <TableCell align="center">
                                                            <IconButton size="small" onClick={() => setEditingTime(jt)}>
                                                                <EditIcon />
                                                            </IconButton>
                                                            <IconButton size="small" color="error" onClick={() => handleDeleteJumahTime(jt.id)}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </TableCell>
                                                    )}
                                                </>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>

                {/* Khateeb Schedule Tab */}
                <TabPanel value={activeTab} index={1}>
                    {/* Month/Year Selection */}
                    <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
                        <Grid item>
                            <IconButton onClick={() => {
                                if (selectedMonth === 1) {
                                    setSelectedMonth(12);
                                    setSelectedYear(prev => prev - 1);
                                } else {
                                    setSelectedMonth(prev => prev - 1);
                                }
                            }}>
                                <NavigateBefore />
                            </IconButton>
                        </Grid>
                        <Grid item xs>
                            <Grid container spacing={2} justifyContent="center">
                                <Grid item xs={6} sm={4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Month</InputLabel>
                                        <Select
                                            value={selectedMonth}
                                            onChange={(e) => setSelectedMonth(e.target.value)}
                                            label="Month"
                                        >
                                            {MONTHS.map((month, index) => (
                                                <MenuItem key={index} value={index + 1}>{month}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Year</InputLabel>
                                        <Select
                                            value={selectedYear}
                                            onChange={(e) => setSelectedYear(e.target.value)}
                                            label="Year"
                                        >
                                            {yearOptions.map((year) => (
                                                <MenuItem key={year} value={year}>{year}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <IconButton onClick={() => {
                                if (selectedMonth === 12) {
                                    setSelectedMonth(1);
                                    setSelectedYear(prev => prev + 1);
                                } else {
                                    setSelectedMonth(prev => prev + 1);
                                }
                            }}>
                                <NavigateNext />
                            </IconButton>
                        </Grid>
                    </Grid>

                    {canModify && (
                        <Box sx={{ mb: 2 }}>
                            {isAddingKhateeb ? (
                                <Paper sx={{ p: 2 }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={3}>
                                            <TextField
                                                fullWidth
                                                label="Date"
                                                type="date"
                                                value={newKhateeb.date}
                                                onChange={(e) => setNewKhateeb({ ...newKhateeb, date: e.target.value })}
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Khutbah</InputLabel>
                                                <Select
                                                    value={newKhateeb.khutbah_order}
                                                    onChange={(e) => setNewKhateeb({ ...newKhateeb, khutbah_order: e.target.value })}
                                                    label="Khutbah"
                                                >
                                                    {jumahTimes.map((jt) => (
                                                        <MenuItem key={jt.khutbah_order} value={jt.khutbah_order}>
                                                            {jt.khutbah_label || `${jt.khutbah_order}`}
                                                        </MenuItem>
                                                    ))}
                                                    {jumahTimes.length === 0 && (
                                                        <>
                                                            <MenuItem value={1}>1st</MenuItem>
                                                            <MenuItem value={2}>2nd</MenuItem>
                                                        </>
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Khateeb Name"
                                                value={newKhateeb.khateeb_name}
                                                onChange={(e) => setNewKhateeb({ ...newKhateeb, khateeb_name: e.target.value })}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <Button
                                                variant="contained"
                                                onClick={handleAddKhateeb}
                                                startIcon={<SaveIcon />}
                                                size="small"
                                                sx={{ mr: 1 }}
                                            >
                                                Save
                                            </Button>
                                            <Button
                                                onClick={() => { setIsAddingKhateeb(false); setNewKhateeb({ date: '', khutbah_order: 1, khateeb_name: '' }); }}
                                                startIcon={<CancelIcon />}
                                                size="small"
                                            >
                                                Cancel
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ) : (
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => setIsAddingKhateeb(true)}
                                >
                                    Add Khateeb
                                </Button>
                            )}
                        </Box>
                    )}

                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Date</strong></TableCell>
                                        {jumahTimes.length > 0 ? (
                                            jumahTimes.map((jt) => (
                                                <TableCell key={jt.khutbah_order} align="center">
                                                    <strong>{jt.khutbah_label || `Khutbah ${jt.khutbah_order}`}</strong>
                                                </TableCell>
                                            ))
                                        ) : (
                                            <>
                                                <TableCell align="center"><strong>1st Khutbah</strong></TableCell>
                                                <TableCell align="center"><strong>2nd Khutbah</strong></TableCell>
                                            </>
                                        )}
                                        {canModify && <TableCell align="center"><strong>Actions</strong></TableCell>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {getFridaysInMonth().map((friday) => {
                                        const dateStr = formatDateToISO(friday);
                                        const dayKhateebs = khateebs.find(k => k.date === dateStr)?.khateebs || [];
                                        const maxOrders = jumahTimes.length > 0 ? jumahTimes.length : 2;

                                        return (
                                            <TableRow key={dateStr}>
                                                <TableCell>{formatDate(friday)}</TableCell>
                                                {Array.from({ length: maxOrders }, (_, i) => {
                                                    const khateeb = dayKhateebs.find(k => k.khutbah_order === (i + 1));
                                                    return (
                                                        <TableCell key={i} align="center">
                                                            {editingKhateeb && khateeb && editingKhateeb.id === khateeb.id ? (
                                                                <TextField
                                                                    size="small"
                                                                    value={editingKhateeb.khateeb_name}
                                                                    onChange={(e) => setEditingKhateeb({ ...editingKhateeb, khateeb_name: e.target.value })}
                                                                    sx={{ minWidth: 120 }}
                                                                />
                                                            ) : khateeb ? (
                                                                <Tooltip title={canModify ? "Click to edit" : ""}>
                                                                    <Chip
                                                                        label={khateeb.khateeb_name}
                                                                        size="small"
                                                                        onClick={canModify ? () => setEditingKhateeb(khateeb) : undefined}
                                                                        sx={{ cursor: canModify ? 'pointer' : 'default' }}
                                                                    />
                                                                </Tooltip>
                                                            ) : (
                                                                <Typography variant="caption" color="text.secondary">-</Typography>
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                                {canModify && (
                                                    <TableCell align="center">
                                                        {editingKhateeb && dayKhateebs.some(k => k.id === editingKhateeb.id) ? (
                                                            <>
                                                                <IconButton size="small" color="primary" onClick={handleUpdateKhateeb}>
                                                                    <SaveIcon />
                                                                </IconButton>
                                                                <IconButton size="small" onClick={() => setEditingKhateeb(null)}>
                                                                    <CancelIcon />
                                                                </IconButton>
                                                            </>
                                                        ) : (
                                                            dayKhateebs.map((k) => (
                                                                <IconButton
                                                                    key={k.id}
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => handleDeleteKhateeb(k.id)}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            ))
                                                        )}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
